import { useEffect, useRef } from 'react'

interface WorldParameterRadarProps {
  parameters: {
    magicLevel: number
    scienceLevel: number
    civilizationLevel: number
    dangerLevel: number
    mysteryLevel: number
    complexityLevel: number
    uniquenessLevel: number
    chaosLevel: number
  }
}

export function WorldParameterRadar({ parameters }: WorldParameterRadarProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    
    // Canvas setup
    const width = canvas.width
    const height = canvas.height
    const centerX = width / 2
    const centerY = height / 2
    const radius = Math.min(width, height) * 0.35
    
    // Clear canvas
    ctx.clearRect(0, 0, width, height)
    
    // Data
    const labels = [
      '魔法',
      '科学技術',
      '文明',
      '危険度',
      '神秘性',
      '複雑さ',
      '独自性',
      '混沌度'
    ]
    
    const values = [
      parameters.magicLevel,
      parameters.scienceLevel,
      parameters.civilizationLevel,
      parameters.dangerLevel,
      parameters.mysteryLevel,
      parameters.complexityLevel,
      parameters.uniquenessLevel,
      parameters.chaosLevel
    ]
    
    const angleStep = (Math.PI * 2) / labels.length
    
    // Draw grid
    ctx.strokeStyle = '#e5e7eb'
    ctx.lineWidth = 1
    
    for (let level = 1; level <= 5; level++) {
      ctx.beginPath()
      for (let i = 0; i <= labels.length; i++) {
        const angle = i * angleStep - Math.PI / 2
        const x = centerX + Math.cos(angle) * radius * (level / 5)
        const y = centerY + Math.sin(angle) * radius * (level / 5)
        
        if (i === 0) {
          ctx.moveTo(x, y)
        } else {
          ctx.lineTo(x, y)
        }
      }
      ctx.stroke()
    }
    
    // Draw axes
    ctx.strokeStyle = '#d1d5db'
    for (let i = 0; i < labels.length; i++) {
      const angle = i * angleStep - Math.PI / 2
      ctx.beginPath()
      ctx.moveTo(centerX, centerY)
      ctx.lineTo(
        centerX + Math.cos(angle) * radius,
        centerY + Math.sin(angle) * radius
      )
      ctx.stroke()
    }
    
    // Draw data polygon
    ctx.fillStyle = 'rgba(99, 102, 241, 0.2)'
    ctx.strokeStyle = 'rgba(99, 102, 241, 0.8)'
    ctx.lineWidth = 2
    
    ctx.beginPath()
    for (let i = 0; i <= labels.length; i++) {
      const index = i % labels.length
      const angle = index * angleStep - Math.PI / 2
      const value = values[index] / 10
      const x = centerX + Math.cos(angle) * radius * value
      const y = centerY + Math.sin(angle) * radius * value
      
      if (i === 0) {
        ctx.moveTo(x, y)
      } else {
        ctx.lineTo(x, y)
      }
    }
    ctx.fill()
    ctx.stroke()
    
    // Draw points
    ctx.fillStyle = 'rgba(99, 102, 241, 1)'
    for (let i = 0; i < labels.length; i++) {
      const angle = i * angleStep - Math.PI / 2
      const value = values[i] / 10
      const x = centerX + Math.cos(angle) * radius * value
      const y = centerY + Math.sin(angle) * radius * value
      
      ctx.beginPath()
      ctx.arc(x, y, 4, 0, Math.PI * 2)
      ctx.fill()
    }
    
    // Draw labels
    ctx.fillStyle = '#374151'
    ctx.font = '12px sans-serif'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    
    for (let i = 0; i < labels.length; i++) {
      const angle = i * angleStep - Math.PI / 2
      const labelRadius = radius + 20
      const x = centerX + Math.cos(angle) * labelRadius
      const y = centerY + Math.sin(angle) * labelRadius
      
      // Adjust text alignment based on position
      if (Math.abs(x - centerX) < 10) {
        ctx.textAlign = 'center'
      } else if (x < centerX) {
        ctx.textAlign = 'right'
      } else {
        ctx.textAlign = 'left'
      }
      
      ctx.fillText(`${labels[i]} (${values[i]})`, x, y)
    }
  }, [parameters])
  
  return (
    <div className="flex flex-col items-center space-y-4">
      <canvas
        ref={canvasRef}
        width={400}
        height={400}
        className="max-w-full h-auto"
      />
      <div className="text-sm text-muted-foreground text-center">
        パラメータレーダーチャート（1-10スケール）
      </div>
    </div>
  )
}